/**
 * Every score Rankbist shows, named the same way everywhere: what it is a score OF, what it
 * measures, how it's worked out, and how to raise it. Used by ScoreInfo, ScoreDial (kind),
 * ScorePill (kind) and BandStat (kind), so a number is never shown without saying which score it is.
 * Keep these true to how the API computes each one.
 */
export type ScoreKind =
  | "content"
  | "article"
  | "health"
  | "site_content"
  | "website"
  | "indexing"
  | "authority"
  | "video"
  | "moment"
  | "image"
  | "lighthouse"
  | "ai_visibility"
  | "sitelinks";

export interface ScoreDef {
  name: string;
  /** What the number is about, e.g. "one page's text". */
  of: string;
  measures: string;
  how: string;
  raise: string;
  /** Where it's made. */
  tool: { label: string; href: string };
}

export const SCORES: Record<ScoreKind, ScoreDef> = {
  content: {
    name: "Content SEO score",
    of: "One page or draft — not the whole website",
    measures: "How well the text is written for search: focus keyword use, title and meta description, headings, length, readability, links and images.",
    how: "Each check earns points; the score is the share of points earned, 0–100.",
    raise: "Open the report and fix the high-impact issues first.",
    tool: { label: "Content checker / Live URL checker", href: "/dashboard/url-check" },
  },
  article: {
    name: "Article content score",
    of: "One generated article",
    measures: "The same Content SEO checks as the Content checker, run on the article the generator wrote.",
    how: "Share of content checks passed, 0–100. The generator rewrites until it reaches the target.",
    raise: "Open the article and apply the suggested fixes, or regenerate.",
    tool: { label: "Article generator", href: "/dashboard/articles" },
  },
  health: {
    name: "Site Health Score",
    of: "The whole website (the pages the audit read)",
    measures: "Technical health: how many pages have no technical errors (broken links, server errors, redirects, missing titles, noindex by mistake…).",
    how: "Share of scanned pages with no errors, 0–100.",
    raise: "Fix the errors in the audit report, starting with the ones on the most pages.",
    tool: { label: "Site Audit", href: "/dashboard/site" },
  },
  sitelinks: {
    name: "Sitelinks readiness",
    of: "The whole website's structure (the pages the audit read)",
    measures: "How clearly the site shows Google its important pages: main menu, clicks from the home page, internal links, WebSite markup, breadcrumbs, unique titles and clean addresses.",
    how: "Seven weighted signals add up to 0–100 (menu 25, click depth 20, internal links 15, WebSite markup 10, breadcrumbs 10, titles 10, addresses 10). Google alone decides whether sitelinks show.",
    raise: "Work through the Sitelinks tab's checklist from the top; each item has the steps and something to copy.",
    tool: { label: "Site Audit → Sitelinks", href: "/dashboard/site" },
  },
  site_content: {
    name: "Average content score",
    of: "Every page the Site Audit read",
    measures: "The average Content SEO score of those pages — how well their text is optimised.",
    how: "Average of each page's content score, 0–100.",
    raise: "Open the pages with the lowest scores in the audit and improve their text.",
    tool: { label: "Site Audit", href: "/dashboard/site" },
  },
  website: {
    name: "Daily website score",
    of: "One website, from last night's checks",
    measures: "All of last night's checks on the site (home page SEO, speed, the audit and more), with serious problems counting more.",
    how: "Share of checks passed (serious checks count more), 0–100.",
    raise: "Do the tasks in the website's daily report.",
    tool: { label: "My websites", href: "/dashboard/websites" },
  },
  indexing: {
    name: "Indexing Health Score",
    of: "One Search Console property",
    measures: "How many of the pages that should be in Google actually are.",
    how: "Indexed pages ÷ (indexed + not indexed + problem pages). Pages you excluded on purpose don't count.",
    raise: "Fix the problem pages first, then help Google find the pages it hasn't visited.",
    tool: { label: "Indexing", href: "/dashboard/indexing" },
  },
  authority: {
    name: "Authority Score",
    of: "A whole domain",
    measures: "How well-linked the site is across the web — our 0–100 scale of its rank in the public web graph.",
    how: "Higher when many strong sites link to it. Changes slowly.",
    raise: "Earn links from relevant, well-linked sites.",
    tool: { label: "Site Explorer", href: "/dashboard/explorer" },
  },
  video: {
    name: "Video SEO score",
    of: "One video's title, description, chapters and tags",
    measures: "How findable the video is in search: keyword in title, description quality, chapters, tags and more.",
    how: "Share of video checks passed, 0–100.",
    raise: "Apply the fixes listed under the score.",
    tool: { label: "Video SEO checker", href: "/dashboard/creator/seo-checker" },
  },
  moment: {
    name: "Moment score",
    of: "One short cut from your video",
    measures: "How well the moment works on its own: a strong hook, a complete point, energy and clarity.",
    how: "Average of those four, 0–100.",
    raise: "Choose another moment or adjust the start and end.",
    tool: { label: "Shorts maker", href: "/dashboard/creator/shorts" },
  },
  image: {
    name: "Image score",
    of: "One image file",
    measures: "Format, file size, dimensions, file name and hidden data (like GPS location).",
    how: "Starts at 100; each problem takes points off.",
    raise: "Download the optimised copy and use the suggested file name and alt text.",
    tool: { label: "Image SEO helper", href: "/dashboard/site#image-seo" },
  },
  ai_visibility: {
    name: "AI visibility score",
    of: "Your brand, across the AI answers Brand Radar read",
    measures: "How often AI assistants name your brand, cite your site as a source, and how early they name you compared with other brands.",
    how: "50% how often you're named, 30% how often your site is cited, 20% your position when named (named first = full points). 0–100.",
    raise: "Work through “What to do next” on the Brand Radar page, starting with the questions where only competitors appear.",
    tool: { label: "Brand Radar", href: "/dashboard/brand-radar" },
  },
  lighthouse: {
    name: "Lighthouse score",
    of: "One page, on mobile or desktop",
    measures: "Google's Lighthouse test: Performance (speed), Accessibility, Best Practices or SEO.",
    how: "Google's own 0–100 score: 90+ good, 50–89 needs work, under 50 poor.",
    raise: "Open the report's opportunities and fixes.",
    tool: { label: "Lighthouse", href: "/dashboard/lighthouse" },
  },
};

export const SCALE = "80–100 strong · 60–79 needs work · under 60 poor";
